import { useGetAllGeneralSettingsListQuery } from '@api/pos/pos-bo-general-settings.api.rtk';
import {
  useGetShift,
  useLazyGetShiftID,
} from '@api/pos/pos-work-period.api.rtk';
import { getColor } from '@assets/theme';
import useAppContext from '@hooks/useAppContext';
import useDisclosure from '@hooks/useDisclosure';
import useGenpar from '@hooks/useGenpar';
import useUser from '@hooks/useUser';
import POSConfirmationDialog from '@modules/point-of-sale/components/pos-selector/pos-confirmation-dialog';
import {
  selectPOSHasSelectRole,
  selectPOSisHaveOrders,
  selectPOSstationID,
  selectPOSstationName,
} from '@modules/point-of-sale/slice/pos-selector';
import { setPOSSelectedStationSlice } from '@modules/point-of-sale/slice/pos-stations.slice';
import { clearPOSTableArea } from '@modules/point-of-sale/slice/pos-table-area.slice';
import { RefreshOutlined } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  IconButton,
  InputAdornment,
  TextField,
} from '@mui/material';
import { useAppDispatch, useAppSelector } from '@store/index';
import { selectPropertyID } from '@store/selector/property.selector';
import { IconBuildingStore } from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

export const StationSelector = ({
  isDashboard,
  handleProceed,
}: {
  isDashboard?: boolean;
  handleProceed?: () => void;
}) => {
  const dispatch = useAppDispatch();
  const { startLoading, stopLoading, startLoadingStatus, stopLoadingStatus } =
    useAppContext();
  const dialogDisclosure = useDisclosure();
  const { white } = getColor();
  const propertyID = useAppSelector(selectPropertyID);
  const isHaveOrders = useAppSelector(selectPOSisHaveOrders);
  const stationID = useAppSelector(selectPOSstationID);
  const posStationName = useAppSelector(selectPOSstationName);
  const pathname = useLocation()?.pathname?.split('/')?.[1];
  const user = useUser();
  const hasSelectRole = useAppSelector(selectPOSHasSelectRole);

  const [getShiftID] = useLazyGetShiftID();

  const genpar = useGenpar({});
  const { refetchGenpar } = genpar;

  const { isLoading: isGetting, isFetching: isReFetching } = useGetShift(
    stationID,
    {
      skip: !stationID || !isDashboard,
      refetchOnMountOrArgChange: true,
    },
  );

  const { data, isFetching, isLoading, isSuccess, refetch } =
    useGetAllGeneralSettingsListQuery(propertyID, {
      skip: !propertyID,
      refetchOnMountOrArgChange: true,
      selectFromResult: ({ data, ...item }) => ({
        ...item,
        data: data && 'results' in data ? data?.results : data,
      }),
    });

  const stationListBOData = useMemo(() => data ?? [], [data]);

  const [initialValue, setInitialValue] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [value, setValue] = useState('');
  const [options, setOptions] = useState<string[]>([]);

  const handleChangeStation = async (stationName: string, runApi?: boolean) => {
    const station = stationListBOData?.find(
      (item) => item.stationName === stationName,
    );

    if (runApi && user?.userID && station?.stationID) {
      startLoadingStatus('Checking Shift');
      await getShiftID({
        userID: user?.userID ?? '',
        stationID: station?.stationID ?? '',
      })
        .unwrap()
        .then(stopLoadingStatus)
        .catch((err) => console.error(err))
        .finally(() => {
          stopLoadingStatus();
        });
    }

    setValue(stationName);

    dispatch(
      setPOSSelectedStationSlice(
        stationName === 'All Stations'
          ? {
              tenantID: '',
              propertyID: '',
              stationID: 'All',
              stationName: 'All Stations',
              ptuNo: '',
              ptuDateStart: '',
              ptuDateExpiry: '',
              ptuMin: '',
              machineSerialNo: '',
              subModuleCode: '',
            }
          : station,
      ),
    );

    if (posStationName !== stationName) {
      dispatch(clearPOSTableArea());
    }

    refetchGenpar();
  };

  useEffect(() => {
    setValue('');
    setInputValue('');
    setOptions([]);
  }, [propertyID]);

  useEffect(() => {
    if (isReFetching || isReFetching) return;

    const newOptions = stationListBOData?.map((item) => item.stationName) ?? [];

    const list =
      pathname === 'back-office' ? ['All Stations', ...newOptions] : newOptions;
    setOptions(list);

    if (list.length === 1 && !hasSelectRole) {
      handleProceed?.();
    }
  }, [stationListBOData, pathname, handleProceed, isGetting, isReFetching]);

  useEffect(() => {
    if (isLoading || isFetching || !isSuccess) return;

    const hasStations = stationListBOData?.length > 0;
    if (!hasStations || value) return;

    const matchedStation = stationListBOData.find(
      (item) => item.stationID === stationID,
    );

    const targetStation = matchedStation ?? stationListBOData[0];
    if (targetStation) {
      handleChangeStation(targetStation.stationName, isDashboard);
    }
  }, [
    stationListBOData,
    isFetching,
    isLoading,
    isSuccess,
    stationID,
    value,
    isDashboard,
  ]);

  const NoOptionsText = () => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        justifyContent: 'space-between',
      }}
    >
      <span>No options found.</span>
      <IconButton
        onClick={async () => {
          startLoading();
          await refetch();
          stopLoading();
        }}
      >
        <RefreshOutlined />
      </IconButton>
    </Box>
  );

  if (isDashboard) {
    return (
      <Autocomplete
        disableClearable
        loading={isLoading || isFetching || isGetting || isReFetching}
        value={value}
        inputValue={inputValue}
        options={options}
        onChange={(_e, newValue) => {
          if (!newValue) return;
          inputValue !== newValue && handleChangeStation(newValue, true);
        }}
        onInputChange={(_, newInputValue) => {
          if (!newInputValue) {
            dispatch(setPOSSelectedStationSlice(undefined));
          }
          setInputValue(newInputValue);
        }}
        renderInput={(params) => (
          <TextField {...params} placeholder='Search Station' />
        )}
        noOptionsText={<NoOptionsText />}
      />
    );
  }

  return (
    <>
      <Autocomplete
        disableClearable
        loading={isLoading || isFetching}
        value={value}
        inputValue={inputValue}
        options={options}
        onChange={async (_e, newValue) => {
          if (!newValue) return;
          if (isHaveOrders) {
            setInitialValue(newValue);
            dialogDisclosure.onOpen();
          } else {
            await handleChangeStation(newValue);
          }
        }}
        onInputChange={(_, newInputValue) => {
          if (!newInputValue) {
            dispatch(setPOSSelectedStationSlice(undefined));
          }
          setInputValue(newInputValue);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder='Search Station'
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position='start'>
                  <IconBuildingStore stroke={1.5} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position='end'>
                  {pathname !== 'point-of-sale' &&
                    params.InputProps.endAdornment}
                </InputAdornment>
              ),
            }}
            sx={{
              width: '200px',
              bgcolor: white,
              '.MuiInputBase-input': {
                pl: 0 + '!important',
              },
              '.Mui-disabled': {
                color: '#000000de',
                opacity: 'unset',
                WebkitTextFillColor: 'unset',
              },
            }}
            size='small'
          />
        )}
        slotProps={{
          popper: {
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, 8],
                },
              },
            ],
            sx: {
              '& .MuiAutocomplete-listbox': {
                p: 0,
              },
              '& .MuiAutocomplete-noOptions': {
                py: 0,
                pr: 0.5,
              },
            },
          },
        }}
        noOptionsText={<NoOptionsText />}
        disabled={pathname === 'point-of-sale'}
      />
      <POSConfirmationDialog
        {...{
          dialogDisclosure,
          onConfirm: async () => {
            if (!initialValue) return;
            await handleChangeStation(initialValue);
            dialogDisclosure.onClose();
          },
        }}
      />
    </>
  );
};
